// Schachserver-Oberfläche. Aufbau wie in lichess: snabbdom rendert, chessground zeichnet das Brett.
import {
  attributesModule,
  classModule,
  eventListenersModule,
  h,
  init,
  propsModule,
  styleModule,
  type VNode,
} from 'snabbdom';
import { libraryView, LibraryCtrl } from './views/library';
import { analyseHead, analyseView, AnalyseCtrl } from './views/analyse';
import { editorView, EditorCtrl } from './views/editor';
import { photoView, PhotoCtrl } from './views/photo';
import { PlayCtrl } from './playctrl';
import { openPlayLink } from './playstart';
import { PuzzleCtrl } from './puzzlectrl';
import { puzzleView } from './views/puzzle';
import { playView } from './views/play';
import { setupView, SetupCtrl } from './views/setup';
import { wallpaperView, WallpaperCtrl } from './views/wallpaper';
import { appPieces, inFocus, isPublic, loadMode, loadPieces, setFocus, watchFullscreen } from './util';
import { t } from './i18n';
import { icon } from './icons';
import { initZoom } from './zoom';
import { parseRoute as parseRouteOf, type Route } from './route';
import { call } from './api';
import { keepAwake } from './appfeel';
import { detectModel } from './device';
import { initInstall } from './install';
import { phoneNav } from './views/phonenav';
import { topNav } from './views/topnav';

const patch = init([classModule, propsModule, attributesModule, styleModule, eventListenersModule]);

const parseRoute = () => parseRouteOf(location.hash, isPublic);

let vnode: VNode | Element = document.getElementById('app')!;
let scheduled = false;
export function redraw() {
  if (scheduled) return;
  scheduled = true;
  const run = () => {
    scheduled = false;
    vnode = patch(vnode, view());
  };
  // Verborgene Tabs bekommen keine Animation-Frames; dann trotzdem zeichnen.
  if (document.hidden) setTimeout(run, 0);
  else requestAnimationFrame(run);
}

const library = new LibraryCtrl(redraw);
const analyse = new AnalyseCtrl(redraw);
const editor = new EditorCtrl(redraw);
const photo = new PhotoCtrl(redraw);
const play = new PlayCtrl(redraw);
const puzzle = new PuzzleCtrl(redraw);
void play.loadBots();
const setup = new SetupCtrl(redraw);
const wallpaper = new WallpaperCtrl(redraw);
let route = parseRoute();

function enter(r: Route) {
  route = r;
  if (inFocus() && r.name !== 'game' && r.name !== 'analyse' && r.name !== 'play' && r.name !== 'puzzle') setFocus(false, redraw);
  if (r.name === 'library') library.load();
  if (r.name === 'game') analyse.openGame(r.id);
  if (r.name === 'analyse') analyse.openFen(r.fen, r.flipped);
  if (r.name === 'editor') editor.open(r.fen, r.unsure, r.flipped);
  if (r.name === 'wallpaper') wallpaper.load();
  if (r.name === 'setup') setup.load();
  // Link aus Analyse oder Bibliothek nur einmal auswerten, danach steht die Adresse wieder auf #/spielen
  if (r.name === 'puzzle') puzzle.open();
  if (r.name === 'play' && openPlayLink(play, r.query)) history.replaceState(null, '', '#/spielen');
  // Engine nur rechnen lassen, solange das Analysebrett offen ist
  if (r.name !== 'game' && r.name !== 'analyse') analyse.ceval.stop();
  redraw();
}

window.addEventListener('hashchange', () => enter(parseRoute()));
document.addEventListener('keydown', e => {
  const board = route.name === 'game' || route.name === 'analyse' || route.name === 'play' || route.name === 'puzzle';
  const typing = e.target instanceof Element && e.target.closest('input, textarea, select');
  // Vollbild: z wie der Zen-Modus von lichess, Esc beendet
  if (board && !typing && !e.metaKey && !e.ctrlKey && !e.altKey && e.key === 'z') {
    e.preventDefault();
    setFocus(!inFocus(), redraw);
    return;
  }
  if (e.key === 'Escape' && inFocus()) setFocus(false, redraw);
  if (route.name === 'game' || route.name === 'analyse') analyse.key(e);
});
initZoom();
watchFullscreen(redraw);

const analyseHref = () => (analyse.gameId !== null ? `#/partie/${analyse.gameId}` : '#/analyse');

function head(): { title: VNode | string; lead: VNode | string } {
  switch (route.name) {
    case 'game':
    case 'analyse':
      return analyseHead(analyse);
    case 'puzzle':
      return { title: t('Taktik'), lead: t('Rätsel nahe deiner Stärke, wahlweise zu einem Thema.') };
    case 'play':
      return { title: t('Spielen'), lead: t('Gegen Bots spielen, aus Fehlern lernen, berühmte Partien nachspielen.') };
    case 'photo':
      return { title: t('Foto'), lead: t('Stellung aus einem Bildschirmfoto oder Buchdiagramm erkennen.') };
    case 'editor':
      return { title: t('Brett-Editor'), lead: t('Stellung aufbauen, prüfen und analysieren.') };
    case 'wallpaper':
      return { title: t('Wallpaper'), lead: t('Partien auf dem schwarzen Desktop, Zug für Zug.') };
    case 'setup':
      return { title: t('Setup'), lead: t('Autostart, Engine, Daten und Lizenzen.') };
    default:
      return { title: t('Bibliothek'), lead: t('Importierte Partien durchsuchen, nachspielen und analysieren.') };
  }
}

/** Titel des Browser-Tabs: bei einer Partie die Spieler, sonst der Seitenname */
function titleText(title: VNode | string): string {
  const g = route.name === 'game' ? analyse.data?.game : undefined;
  if (g) return `${g.white ?? '?'} – ${g.black ?? '?'}`;
  return typeof title === 'string' ? title : t('Analyse');
}

function view(): VNode {
  const body =
    route.name === 'game' || route.name === 'analyse'
      ? analyseView(analyse)
      : route.name === 'editor'
        ? editorView(editor)
        : route.name === 'play'
          ? playView(play)
          : route.name === 'puzzle'
          ? puzzleView(puzzle)
          : route.name === 'photo'
          ? photoView(photo)
          : route.name === 'setup'
            ? setupView(setup)
            : route.name === 'wallpaper'
              ? wallpaperView(wallpaper)
              : libraryView(library);
  const hd = head();
  // Brett-Seiten: schmales Titelband, damit das Brett die Höhe bekommt
  const boardPage = route.name === 'game' || route.name === 'analyse' || route.name === 'play' || route.name === 'puzzle' || route.name === 'editor';
  document.title = `${titleText(hd.title)} · Schachserver`;
  // Bildschirm an, solange gespielt oder gerätselt wird
  void keepAwake((route.name === 'play' && ((play.started && !play.result) || (!!play.guess && !play.guess.over)))
    || (route.name === 'puzzle' && (puzzle.state === 'play' || puzzle.state === 'wrong')));
  const focus = inFocus() && (route.name === 'game' || route.name === 'analyse' || route.name === 'play');
  return h(`div#app.set-${appPieces()}`, { class: { focus } }, [
    focus ? h('button.focus-exit', { attrs: { title: t('Vollbild beenden (Esc)'), 'aria-label': t('Vollbild beenden (Esc)') }, on: { click: () => setFocus(false, redraw) } }, icon('close')) : null,
    topNav(route.name, analyseHref(), typeof hd.title === 'string' ? hd.title : t('Analyse'), redraw),
    h('div.band', { class: { compact: boardPage } }, h('div.inner', [h('h1', hd.title), h('p.lead', hd.lead)])),
    h('main', { class: { tight: boardPage } }, [body]),
    h('footer.foot', h('div.inner', [
      h('span.wordmark', 'SCHACHSERVER'),
      h('span', isPublic ? t('Öffentliche Demo · Bibliothek nur lesend · GPL-3.0') : t('Läuft offline auf diesem Rechner · GPL-3.0')),
    ])),
    phoneNav(route.name, analyseHref(), redraw),
  ]);
}

await loadMode();
route = parseRoute();
enter(route);
loadPieces(redraw);
initInstall(redraw);
void call('POST', '/api/visit').catch(() => {}); // Aufruf zählen (Setup → Aufrufe)
void detectModel().then(redraw);
